import { Box, Button, CircularProgress, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';

import { MARKET_TYPE_EXCLUSION_TYPE_OPTIONS, MARKET_TYPE_USED_FOR_OPTIONS } from '../../../../../../constants';
import FormSelect from '../../../../../molecules/FormSelect';
import RetryAlert from '../../../../../molecules/RetryAlert';
import RemoveExclusionButton from '../RemoveExclusionButton';
import { SelectorProvider, useSelectors } from './SelectorContext';
import {
  formatBonusMarketTypeExclusionSelector,
  getBonusMarketTypeExclusionSelectorKey,
} from '../marketExclusions.utils';

const styles = {
  section: { border: 1, borderColor: 'divider', borderRadius: 1, p: 2 },
  controls: {
    alignItems: 'start',
    display: 'grid',
    gap: 1,
    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr)) auto' },
  },
  list: { borderTop: 1, borderColor: 'divider' },
} as const;

const SelectorControls = () => {
  const { actions, meta } = useSelectors();

  return (
    <Box
      component='form'
      aria-label='Market exclusion selector controls'
      onSubmit={actions.addSelector}
      sx={styles.controls}
    >
      <FormSelect
        label='Selector sport'
        id='welcome-bonus-selector-sportId'
        name='sportId'
        control={meta.control}
        options={meta.sportOptions}
        disabled={meta.isSaving}
        size='small'
      />
      <FormSelect
        label='Selector used for'
        id='welcome-bonus-selector-usedFor'
        name='usedFor'
        control={meta.control}
        options={MARKET_TYPE_USED_FOR_OPTIONS}
        disabled={meta.isSaving}
        size='small'
      />
      <FormSelect
        label='Selector type'
        id='welcome-bonus-selector-type'
        name='type'
        control={meta.control}
        options={MARKET_TYPE_EXCLUSION_TYPE_OPTIONS}
        disabled={meta.isSaving}
        size='small'
      />
      <Stack direction='row' spacing={1}>
        <Button type='submit' variant='outlined' disabled={meta.isDuplicate || meta.isSaving}>
          Add
        </Button>
        <Button type='button' variant='contained' onClick={actions.save} disabled={!meta.isDirty || meta.isSaving}>
          Save
        </Button>
      </Stack>
    </Box>
  );
};

const SelectorList = () => {
  const { state, actions, meta } = useSelectors();

  if (!state.selectors.length) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No market exclusion selectors configured.
      </Typography>
    );
  }

  return (
    <List dense disablePadding sx={styles.list}>
      {state.selectors.map((selector) => {
        const label = formatBonusMarketTypeExclusionSelector(selector, meta.sportsById);
        const key = getBonusMarketTypeExclusionSelectorKey(selector);

        return (
          <ListItem key={key} disableGutters>
            <ListItemText primary={label} />
            <RemoveExclusionButton
              label={`Remove selector ${label}`}
              disabled={meta.isSaving}
              onClick={() => actions.removeSelector(key)}
            />
          </ListItem>
        );
      })}
    </List>
  );
};

const SelectorEditorContent = () => {
  const { actions, meta } = useSelectors();

  return (
    <Box component='section' aria-labelledby='market-exclusion-selectors-title' sx={styles.section}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography id='market-exclusion-selectors-title' variant='subtitle1' sx={{ fontWeight: 700 }}>
            Market exclusion selectors
          </Typography>
        </Stack>

        {meta.isLoading && (
          <Stack sx={{ alignItems: 'center', py: 2 }}>
            <CircularProgress size={24} aria-label='Loading market exclusion selectors' />
          </Stack>
        )}

        {meta.isError && <RetryAlert onRetry={actions.retry}>Unable to load market exclusion selectors.</RetryAlert>}

        {meta.isReady && (
          <>
            <SelectorControls />
            <SelectorList />
          </>
        )}
      </Stack>
    </Box>
  );
};

const SelectorEditor = () => (
  <SelectorProvider>
    <SelectorEditorContent />
  </SelectorProvider>
);

export default SelectorEditor;
